import { skipToken } from "@reduxjs/toolkit/query";
import { selectPopulationFilters } from "@repo/app-types/entities/filters";
import {
  asPlaybookId,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import type { TranscriptMatchesByGrading } from "@repo/app-types/entities/teammatePerformanceSummary";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { type FC, useMemo } from "react";
import { useParams } from "react-router";
import {
  usePlaybookAllMostRecentGradingsCountQuery,
  usePlaybookAllMostRecentGradingsInfiniteQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import {
  PageBody,
  PageLayout,
  PageLayoutHeight,
  TableCard,
} from "../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../components/performancePlaybookSwitcher.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import {
  buildTeamPerformancePageQueryArgs,
  checkHasNarrowingFilters,
} from "../filters/buildFilterQueryArgs.js";
import { useFilters } from "../filters/filters.js";
import { usePlaybookFilterDefaults } from "../filters/usePlaybookFilterDefaults.js";
import {
  buildGradingModalPath,
  buildTranscriptSearchNavState,
} from "./gradingHelpers.js";
import { GradingsTable } from "./gradingsTable.js";
import { DisplayColumnsSelector } from "./hooks/displayColumnsSelector.js";
import { useCallsMetricColumnSelector } from "./hooks/useCallsMetricColumnSelector.js";
import { useCustomFieldColumnSelector } from "./hooks/useCustomFieldColumnSelector.js";
import { useSectionColumnSelector } from "./hooks/useSectionColumnSelector.js";
import { useTranscriptSearch } from "./hooks/useTranscriptSearch.js";

export const TrainingTeamPageByCustomer: FC = () => {
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param.");
  }
  const playbookId = asPlaybookId(playbookIdParam);

  const modalNavigation = useModalNavigation();

  const now = useNow();
  const { data: playbook } = usePlaybookByIdQuery(playbookId);
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  const isCustomerExperienceScoreEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.CustomerExperienceScore,
  );

  const playbookFilterDefaults = usePlaybookFilterDefaults(playbookId, "calls");
  const [
    {
      teammateGroupIds,
      dateRange,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      recordingCount,
      dateBasis,
    },
    renderFilters,
    hasNarrowingChanges,
  ] = useFilters(
    [
      "teammateGroupIds",
      "dateRange",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "recordingCount",
      "dateBasis",
    ],
    { playbookId, view: "calls" },
    playbookFilterDefaults,
  );

  const {
    selectedMetricColumnIds,
    selectSections: metricColumnSections,
    selectedIds: metricColumnSelectedIds,
    onSelectionChange: onMetricColumnSelectionChange,
  } = useCallsMetricColumnSelector({
    playbook,
    isCustomerExperienceScoreEnabled,
  });

  const {
    selectedCustomFieldDefinitions,
    selectSections: customFieldSections,
    selectedIds: customFieldSelectedIds,
    onSelectionChange: onCustomFieldSelectionChange,
  } = useCustomFieldColumnSelector({
    includeDataExtractionFields: true,
    preferenceScope: { type: "default" },
    playbook,
    view: "calls",
  });

  const {
    showsPlaybookColumn,
    selectedSections,
    selectSections: sectionColumnSections,
    selectedIds: sectionColumnSelectedIds,
    onSelectionChange: onSectionSelectionChange,
  } = useSectionColumnSelector({ playbook, view: "calls" });

  const { search, renderSearchInput, deepSearchEnabled } =
    useTranscriptSearch();

  const baseArgs =
    teammateGroupIds !== undefined
      ? buildTeamPerformancePageQueryArgs(now, {
          playbookId,
          dateRange,
          teammateGroupIds,
          outcome,
          customFields,
          playbookConditions,
          signalId,
          signalMetadataFields,
          totalRecordingDuration,
          recordingCount,
          dateBasis,
        })
      : undefined;

  const deepSearchEnabledArg = search ? deepSearchEnabled : undefined;

  // The "N of M" denominator: the same population the list is drawn from
  // (so it follows "Include calls without grades") minus the user's own
  // filters. Suppressed unless the user narrowed something that reaches the
  // filters, where the two queries would be identical.
  const showsDenominator =
    baseArgs !== undefined &&
    hasNarrowingChanges &&
    checkHasNarrowingFilters(baseArgs.filters);

  const populationArgs =
    baseArgs && showsDenominator
      ? {
          ...baseArgs,
          filters: selectPopulationFilters(baseArgs.filters),
          search,
          deepSearchEnabled: deepSearchEnabledArg,
        }
      : skipToken;

  const filteredArgs = baseArgs
    ? { ...baseArgs, search, deepSearchEnabled: deepSearchEnabledArg }
    : skipToken;

  // `currentData`, not `data`: the latter falls back to the previous args'
  // result whenever the current request isn't successful, which for the
  // denominator means a failed refetch can leave M behind N ("80 of 45").
  const { currentData: totalGradingCount, isFetching: isFetchingTotalCount } =
    usePlaybookAllMostRecentGradingsCountQuery(populationArgs);
  const { data: gradingCount, isFetching: isFetchingCount } =
    usePlaybookAllMostRecentGradingsCountQuery(filteredArgs);

  const {
    data: gradingsData,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePlaybookAllMostRecentGradingsInfiniteQuery(filteredArgs);

  // Reflects when the gradings count/list is being (re)fetched due to a change in filters
  const isReloading =
    isFetchingTotalCount ||
    isFetchingCount ||
    (isFetching && !isFetchingNextPage);

  const gradings = useMemo(
    () => gradingsData?.pages.flatMap((page) => page.recentGradings) ?? [],
    [gradingsData],
  );

  const snippetsByGradingId = useMemo(
    () =>
      Object.assign(
        {},
        ...(gradingsData?.pages.map((page) => page.snippets) ?? []),
      ) as TranscriptMatchesByGrading,
    [gradingsData],
  );

  return (
    <PageLayout height={PageLayoutHeight.Bounded}>
      <PageHeader
        title="Calls"
        filterBar={renderFilters({
          isLoading: isReloading,
          // Explicitly gated rather than relying on the skipped query: RTK
          // Query's `data` keeps serving the last successful result once args
          // become `skipToken`, which would strand a stale denominator.
          denominatorCount: showsDenominator ? totalGradingCount : undefined,
          count: gradingCount,
          leadingItems: (
            <>
              {renderSearchInput({
                resultCount: gradingCount,
                isLoadingResults: isReloading,
              })}
              <PerformancePlaybookSwitcher />
            </>
          ),
          rightActions: (
            <DisplayColumnsSelector
              playbookId={playbookId}
              view="calls"
              viewDefaults={playbook?.view_defaults.calls}
              metricColumnSections={metricColumnSections}
              metricColumnSelectedIds={metricColumnSelectedIds}
              onMetricColumnSelectionChange={onMetricColumnSelectionChange}
              sectionColumnSections={sectionColumnSections}
              sectionColumnSelectedIds={sectionColumnSelectedIds}
              onSectionColumnSelectionChange={onSectionSelectionChange}
              customFieldSections={customFieldSections}
              customFieldSelectedIds={customFieldSelectedIds}
              onCustomFieldSelectionChange={onCustomFieldSelectionChange}
            />
          ),
        })}
      />

      <PageBody fills>
        <TableCard>
          <GradingsTable
            selectedMetricColumnIds={selectedMetricColumnIds}
            outcomeDefinition={outcomeDefinition}
            isTeammateVisible
            gradings={gradings}
            snippetsByGradingId={snippetsByGradingId}
            isLoading={isReloading}
            isError={isError}
            onLoadMore={hasNextPage ? fetchNextPage : undefined}
            onGradingAction={(grading) => {
              const gradingSnippets = snippetsByGradingId[grading.id];
              modalNavigation.push(
                buildGradingModalPath(grading.id),
                search?.trim() && gradingSnippets?.length
                  ? buildTranscriptSearchNavState(search, gradingSnippets)
                  : undefined,
              );
            }}
            selectedCustomFieldDefinitions={selectedCustomFieldDefinitions}
            showsPlaybookColumn={showsPlaybookColumn}
            selectedSections={selectedSections}
            showsCustomerExperienceColumn={isCustomerExperienceScoreEnabled}
            isFullBleed
          />
        </TableCard>
      </PageBody>

      <NavigationSlideoverContainer />
    </PageLayout>
  );
};
